import React, { useCallback, useEffect, useState } from 'react';
import { carteraApi } from './api';

/**
 * Switch GLOBAL del bot de ventas — enciende/apaga el bot para TODOS los leads.
 *
 * ── El problema que resuelve ─────────────────────────────────────────────
 * La V1 del bot ya contesta sola. Lo que faltaba no era producto, era el botón
 * de apagado: cuando el bot dice algo que no debe, el operador tiene que poder
 * callarlo YA, sin esperar un deploy. Este switch escribe `bot_ventas_configs.activo`
 * (GET/POST /bot-ventas/config) y nada más.
 *
 * ── Por qué vive junto al selector Cobranza/Cotizaciones ─────────────────
 * §4.4: un switch en una pantalla de configuración aparte no se usa —para cuando
 * el operador se acuerda de que existe, ya contestó a mano—. Va donde se mira la
 * conversación, y SOLO en la pestaña de cotizaciones: en cobranza no hay bot de
 * ventas que apagar.
 *
 * ── La regla que no se rompe (§5.2) ──────────────────────────────────────
 * 🔴 El switch NUNCA se queda mostrando la posición nueva si el backend no la
 * confirmó. No hay estado optimista: mientras el POST está en vuelo se muestra un
 * spinner y la posición ANTERIOR; solo cuando la respuesta llega se repinta con
 * lo que el backend dice que quedó guardado. Un switch que miente sobre si el bot
 * está encendido es peor que no tener switch.
 *
 * 🔴 Apagar el bot NO silencia el número (§4.1): el webhook sigue guardando los
 * mensajes entrantes. Lo único que se apaga es la respuesta automática. Y este
 * switch solo toca `activo`; jamás `conversacion.modo`, que es la columna
 * compartida con cobranza.
 *
 * ── Encendido efectivo vs. switch ────────────────────────────────────────
 * El backend distingue `config.activo` (lo que pinta el switch) de
 * `entorno.habilitado_efectivo` (si el bot puede contestar de verdad; el env lo
 * puede tener apagado aunque el switch diga que sí). La banda de aviso se decide
 * con el estado EFECTIVO: si el bot no está atendiendo —da igual por qué—, hay
 * que avisarlo de forma imposible de ignorar.
 */
export const BotVentasSwitch = () => {
  // `config`/`entorno` son lo ÚLTIMO confirmado por el backend. El render se hace
  // siempre con esto, nunca con la intención del clic.
  const [config, setConfig] = useState(null);
  const [entorno, setEntorno] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  // Confirmación al APAGAR: apagar el bot para todos es una acción con
  // consecuencias (leads sin respuesta), así que pide un paso extra. Encender no
  // necesita confirmación —es el estado seguro—.
  const [confirmando, setConfirmando] = useState(false);

  const cargar = useCallback(async (signal) => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await carteraApi.getBotVentasConfig(signal);
      setConfig(data?.config || null);
      setEntorno(data?.entorno || null);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || signal?.aborted) return;
      setError(mensajeDeError(e, 'No se pudo leer el estado del bot de ventas.'));
    } finally {
      if (!signal?.aborted) setCargando(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    cargar(controller.signal);
    return () => controller.abort();
  }, [cargar]);

  // El único punto que escribe. `activoDeseado` viene del clic; el estado local
  // solo se mueve DESPUÉS de que el backend confirme, con el shape que devuelve.
  const guardar = async (activoDeseado) => {
    if (guardando) return;
    setGuardando(true);
    setError(null);
    setConfirmando(false);
    try {
      const { data } = await carteraApi.actualizarBotVentasActivo(activoDeseado);
      // 🔴 Se repinta con lo que respondió el backend, no con `activoDeseado`. Si
      // el backend, por lo que sea, guardó otra cosa, el switch muestra ESO.
      setConfig(data?.config || null);
      setEntorno(data?.entorno || null);
    } catch (e) {
      // El switch se queda donde estaba: `config` no se tocó. La posición no
      // miente porque nunca la adelantamos.
      setError(mensajeDeError(e, 'No se pudo cambiar el estado del bot.'));
    } finally {
      setGuardando(false);
    }
  };

  const onClickSwitch = () => {
    if (guardando || cargando) return;
    const activoAhora = Boolean(config?.activo);
    if (activoAhora) {
      // Apagar → pedir confirmación primero.
      setConfirmando(true);
      return;
    }
    guardar(true);
  };

  // Mientras carga el estado inicial no se pinta un switch a medias: sin dato
  // confirmado, no hay posición honesta que mostrar.
  if (cargando && !config) {
    return (
      <div style={ESTILO.fila} aria-busy="true">
        <span className="pi pi-spin pi-spinner" style={{ fontSize: 12, color: '#64748B' }} />
        <span style={{ fontSize: 12, color: '#64748B' }}>Cargando estado del bot…</span>
      </div>
    );
  }

  const activo = Boolean(config?.activo);
  // El bot atiende de verdad solo si el switch está ON y el entorno lo permite.
  // Si el backend no mandó `habilitado_efectivo`, cae al valor del switch.
  const atendiendo = entorno?.habilitado_efectivo !== undefined
    ? Boolean(entorno.habilitado_efectivo)
    : activo;

  return (
    <div>
      <div style={ESTILO.fila}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>Bot de ventas</span>

        <button
          type="button"
          role="switch"
          aria-checked={activo}
          aria-label="Bot de ventas"
          disabled={guardando}
          onClick={onClickSwitch}
          title={activo ? 'El bot contesta a los leads. Clic para apagarlo.' : 'El bot está apagado. Clic para encenderlo.'}
          style={{
            position: 'relative', width: 42, height: 23, borderRadius: 999,
            border: 'none', cursor: guardando ? 'default' : 'pointer',
            background: activo ? '#22C55E' : '#CBD5E1',
            opacity: guardando ? 0.6 : 1, transition: 'background .15s', flexShrink: 0,
          }}
        >
          <span
            style={{
              position: 'absolute', top: 2, left: activo ? 21 : 2,
              width: 19, height: 19, borderRadius: '50%', background: '#fff',
              transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {guardando && <span className="pi pi-spin pi-spinner" style={{ fontSize: 10, color: '#64748B' }} />}
          </span>
        </button>

        <span style={{ fontSize: 11, fontWeight: 600, color: activo ? '#15803D' : '#64748B' }}>
          {guardando ? 'Guardando…' : (activo ? 'Encendido' : 'Apagado')}
        </span>
      </div>

      {/* Confirmación al apagar: un paso extra deliberado. */}
      {confirmando && (
        <div role="dialog" aria-label="Confirmar apagado del bot" style={ESTILO.confirm}>
          <div style={{ fontSize: 12, color: '#7A2E0E', lineHeight: 1.4, marginBottom: 8 }}>
            ¿Apagar el bot para <b>todos</b> los leads? Los mensajes seguirán llegando,
            pero nadie recibirá respuesta automática hasta que lo enciendas.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => guardar(false)} disabled={guardando} style={ESTILO.btnPeligro}>
              Sí, apagar
            </button>
            <button type="button" onClick={() => setConfirmando(false)} disabled={guardando} style={ESTILO.btnCancelar}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 11, color: '#B42318', lineHeight: 1.35 }}>
          {error}
        </div>
      )}

      {/* Banda de aviso mientras el bot NO está atendiendo. Se decide por el
          estado EFECTIVO: da igual si es el switch o el env, el operador tiene
          que verlo. No se pinta mientras se está guardando para no parpadear. */}
      {!atendiendo && !guardando && (
        <div role="status" style={ESTILO.banda}>
          <span className="pi pi-power-off" style={{ marginRight: 7 }} />
          El bot de ventas está apagado. Los leads no reciben respuesta automática;
          los mensajes se siguen guardando y hay que contestarlos a mano.
        </div>
      )}
    </div>
  );
};

/**
 * Mensaje legible. El endpoint responde 409 cuando la migración no se aplicó
 * —estado esperado, no un bug— y ahí conviene mostrar el texto del backend tal
 * cual porque explica QUÉ falta.
 */
const mensajeDeError = (error, porDefecto) => {
  const respuesta = error?.response;
  if (respuesta?.data?.message) return respuesta.data.message;
  if (!respuesta) return 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.';
  return porDefecto;
};

const ESTILO = {
  fila: { display: 'flex', alignItems: 'center', gap: 8 },
  confirm: {
    marginTop: 8, padding: '10px 11px', borderRadius: 10,
    background: '#FFF6E5', border: '1px solid #F4E2BC',
  },
  banda: {
    marginTop: 8, padding: '9px 12px', borderRadius: 9,
    background: '#FBE7E7', border: '1px solid #F2CACA',
    color: '#B91C1C', fontSize: 11.5, fontWeight: 600, lineHeight: 1.35,
  },
  btnPeligro: {
    border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
    background: '#DC2626', color: '#fff', fontSize: 11.5, fontWeight: 700,
  },
  btnCancelar: {
    border: '1px solid #E7EBF0', borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
    background: '#fff', color: '#475569', fontSize: 11.5, fontWeight: 600,
  },
};
